/**
 * StateCredentials — /states/credentials
 */
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStateContext } from "@/hooks/useStateContext";
import { RefreshCw } from "lucide-react";
import client from "@/api/client";
import ClimateCredentialsPanel from "@/components/ClimateCredentialsPanel";
import { PageHeader } from "@/components/ds";

export default function StateCredentials() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // V100: State context provided by StateLayout/useStateContext
  const { stateId, geography: geo, stateParams } = useStateContext();

  const load = (g = geo) => {
    setLoading(true);
    // Adapt state credentials to ClimateCredentialsPanel format
    client.get(`/states/credentials?geography=${g}`).then(r => {
      const d = r.data;
      // Map to ClimateCredentialsPanel expected shape
      setData({
        workspace_type: "STATES",
        organization: `${g} State Portfolio`,
        sector: "Multi-sector",
        country: g,
        methodology_version: "DCI-STATE-AGGREGATION-V1.0",
        calculation_hash: d.credential_id || "N/A",
        period: `${new Date().getFullYear()} Aggregate`,
        is_locked: false,
        section_1_summary: {
          total_tco2e: d.summary?.total_footprint_tco2e || 0,
          scope1_tco2e: d.summary?.total_footprint_tco2e || 0,
          scope2_tco2e: 0,
          scope3_tco2e: 0,
          confidence_score: d.data_quality?.confidence_score || 0,
          confidence_tier: d.data_quality?.confidence_score >= 75 ? "High Confidence" : "Moderate Confidence",
          decision_outcome: "State Portfolio Aggregation",
          decision_color: "teal",
          intensity_metrics: {},
        },
        section_2_methodology: {
          primary_standard: d.methodology?.standard || "GHG Protocol",
          scope_definition: d.methodology?.scope || "Operational control",
          scope2_method: "Location-based",
          methodology_version: "DCI-STATE-AGGREGATION-V1.0",
          calculation_hash: d.credential_id,
          ifrs_s2_aligned: false,
          tcfd_aligned: false,
          ghg_protocol: true,
          references: ["GHG Protocol Corporate Standard", "IPCC 2006 Guidelines", "IEA 2023"],
        },
        section_3_breakdown: {
          scope1_tco2e: d.summary?.total_footprint_tco2e || 0,
          scope2_tco2e: 0,
          scope3_tco2e: 0,
          total_tco2e: d.summary?.total_footprint_tco2e || 0,
          scope1_share_pct: 100,
          scope2_share_pct: 0,
          scope3_share_pct: 0,
        },
        section_4_data_quality: {
          confidence_score: d.data_quality?.confidence_score || 0,
          confidence_tier: "Moderate Confidence",
          evidence_coverage_pct: d.data_quality?.coverage_pct || 0,
          critical_anomalies: 0,
          period_locked: false,
        },
        section_5_evidence: {
          verification_status: d.data_quality?.verification_status || "Platform-aggregated",
          calculation_hash: d.credential_id,
          lock_status: "State aggregation — individual periods locked separately",
          vvb_required_for: ["Official state climate inventory", "Regulatory reporting"],
        },
        section_6_risk: {
          anomaly_flags: [],
          assumption_flags: [],
          uncertainty_note: d.methodology?.limitation || "Aggregation covers registered entities only.",
          limitations: [
            d.methodology?.limitation || "",
            "Individual entity data may not be third-party verified.",
          ],
        },
        section_7_decision: {
          decision: { outcome_label: "State Portfolio View", badge_color: "teal", description: d.decision?.description || "" },
          confidence_tier: "Moderate Confidence",
          institutional_readiness: "Portfolio Aggregation",
        },
        disclaimer: d.disclaimer,
      });
    }).catch(() => {}).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <PageHeader
        label="STATES WORKSPACE"
        title="State Climate & Circularity Credentials"
        subtitle={`Aggregated governance credentials for ${geo}`}
        backUrl="/states/overview"
        backLabel="State Overview"
      />
      <ClimateCredentialsPanel data={data} loading={loading} onRefresh={() => load()} />
    </div>
  );
}
